import {
  Check,
  Disc2,
  HelpCircle,
  MessageSquare,
  Phone,
  Plus,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  createAgent,
  createIntent,
  createQuestion,
  createScript,
  deleteIntent,
  deleteQuestions,
  deleteScript,
  getAgent,
  getIntents,
  getQuestions,
  getScripts,
  updateAgent,
  updateIntent,
  updateQuestion,
  updateScript,
} from "../../api/compaign";
import { normalizeTime } from "../../helper/function";

const CampaignSetupWizard = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [phoneError, setPhoneError] = useState("");
  const [step4Data, setStep4Data] = useState({
    category: "greeting",
    description: "",
  });
  const [scripts, setScripts] = useState([]);
  const [editingScriptId, setEditingScriptId] = useState(null);
  const [isLoadingScripts, setIsLoadingScripts] = useState(false);

  const handleStep4Change = (e) => {
    const { name, value } = e.target;
    setStep4Data({
      ...step4Data,
      [name]: value,
    });
  };

  // Script management functions are defined below
  const [formErrors, setFormErrors] = useState({});
  const [voiceAgentId, setVoiceAgentId] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const agentId = searchParams.get("agent");
  const [campaignDetails, setCampaignDetails] = useState({
    name: "",
    description: "",
    agentType: "buyer",
    callerId: "",
    startTime: "09:00",
    endTime: "17:00",
    daysAllowed: {
      Monday: false,
      Tuesday: false,
      Wednesday: false,
      Thursday: false,
      Friday: false,
      Saturday: false,
      Sunday: false,
    },
    callThrottleLimit: 500,
    voiceTone: "Friendly",
  });

  // No pre-configuration needed - Voice Agent is created directly
  // VAPI credentials are configured via environment variables on the backend
  // console.log(accounts);
  useEffect(() => {
    let agent = localStorage.getItem("voice_agent");
    // if (JSON.parse(agent)) {
    //   setVoiceAgentId(JSON.parse(agent));
    //   // console.log({ agentIDsss:JSON.parse(agent) });
    // }
    // return () => {
    //   localStorage.setItem("voice_agent", null);
    // };
  }, []);
  // console.log(voiceAgentId);
  useEffect(() => {
    if (agentId) {
      setVoiceAgentId(agentId);
      localStorage.setItem("voice_agent", agentId);
      getAgent(agentId)
        .then((response) => {
          // console.log(response.data);

          const campaignData = response.data;
          const days_allowed = response.data?.days_allowed || "";

          const allDays = [
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday",
            "Saturday",
            "Sunday",
          ];
          // console.log({ campaignData });
          const allowedDays = days_allowed?.toLowerCase().split(",");

          const daysAllowed = {};

          allDays.forEach((day) => {
            daysAllowed[day] = allowedDays.includes(day?.toLowerCase());
          });

          setCampaignDetails({
            name: campaignData.name,
            description: campaignData.description,
            agentType: campaignData.agent_type || "buyer",
            callerId: campaignData.phone_number,
            startTime: campaignData.start_time,
            endTime: campaignData.end_time,
            daysAllowed,
            callThrottleLimit: campaignData.max_call_limit,
            voiceTone: campaignData.voice_tone,
          });

        })
        .catch((error) => {
          if (error.status === 404) {
            toast.error(
              error.response?.data?.detail || "Failed to load agent details"
            );
            navigate("/voice-agents");
            return;
          }

          console.error("Error fetching agent:", error);
          toast.error(
            error.response?.data?.detail || "Failed to load agent details"
          );
        });
    }
  }, [agentId]);

  const [qualificationQuestions, setQualificationQuestions] = useState([]);
  const [newQuestion, setNewQuestion] = useState({
    prompt: "",
    aiHints: "",
  });
  const [editingQuestionId, setEditingQuestionId] = useState(null);

  const [intents, setIntents] = useState([
    {
      id: "default-hot",
      name: "hot",
      //   label: "Owner says he is willing to sell",
      criteria: "Prospect is ready to sell within 3 months",
      enableLiveTransfer: true,
    },
  ]);
  const [newIntent, setNewIntent] = useState({
    name: "",
    label: "",
    criteria: "",
    enableLiveTransfer: false,
  });
  const [editingIntentId, setEditingIntentId] = useState(null);
  const [globalSettings, setGlobalSettings] = useState({
    enableLiveTransfer: false,
    callOutcomeLogging: true,
  });

  useEffect(() => {
    if (agentId || voiceAgentId) {
      const id = agentId || voiceAgentId;
      // Fetch questions
      getQuestion(id);
      // Fetch intents
      getIntent(id);

      getScript(id);
    }
  }, [agentId, voiceAgentId]);
  const getIntent = async (id = agentId || voiceAgentId) => {
    getIntents(id)
      .then((res) => {
        setIntents(
          (res.data || []).map((i) => ({
            id: i.id,
            name: i.name,
            label: i.description,
            criteria: i.description, // or i.criteria if available
            enableLiveTransfer: i.agent_call_transfer,
          }))
        );
      })
      .catch(() => setIntents([]));
  };
  const getQuestion = async (id = agentId || voiceAgentId) => {
    getQuestions(id)
      .then((res) => {
        setQualificationQuestions(
          (res.data || []).map((q) => ({
            id: q.id,
            prompt: q.question,
            aiHints: q.answer_tip,
          }))
        );
      })
      .catch(() => setQualificationQuestions([]));
  };
  const getScript = async (id = agentId || voiceAgentId) => {
    // Fetch scripts
    setIsLoadingScripts(true);
    getScripts(id)
      .then((res) => {
        setScripts(res.data || []);
        // If there's a script, pre-populate step4Data with the first script
        if (res.data && res.data.length > 0) {
          const firstScript = res.data[0];
          // setStep4Data({
          //   category: firstScript.category?.toLowerCase(),
          //   description: firstScript?.description,
          // });
          // setEditingScriptId(firstScript.id);
        }
      })
      .catch((error) => {
        console.error("Error fetching scripts:", error);
        setScripts([]);
      })
      .finally(() => {
        setIsLoadingScripts(false);
      });
  };

  const handleCampaignDetailsChange = (e) => {
    const { name, value } = e.target;
    setCampaignDetails({
      ...campaignDetails,
      [name]: value,
    });
  };

  const handleDayToggle = (day) => {
    setCampaignDetails({
      ...campaignDetails,
      daysAllowed: {
        ...campaignDetails.daysAllowed,
        [day]: !campaignDetails.daysAllowed[day],
      },
    });
  };

  const handleQuestionInputChange = (e) => {
    const { name, value } = e.target;
    setNewQuestion({
      ...newQuestion,
      [name]: value,
    });
  };

  const handleAddQuestion = async () => {
    if (newQuestion.prompt?.trim()) {
      if (editingQuestionId) {
        // Update existing question
        try {
          const id = agentId || voiceAgentId;
          await updateQuestion(id, editingQuestionId, {
            question: newQuestion.prompt,
            answer_tip: newQuestion.aiHints,
          });
          setEditingQuestionId(null);
          setQualificationQuestions(
            qualificationQuestions.map((q) =>
              q.id === editingQuestionId
                ? { ...newQuestion, id: editingQuestionId }
                : q
            )
          );
          getQuestion();
          toast.success("Question updated");
        } catch (e) {
          toast.error("Failed to update question");
        }
        setEditingQuestionId(null);
      } else {
        // Add new question
        try {
          const id = agentId || voiceAgentId;
          const res = await createQuestion(id, {
            question: newQuestion.prompt,
            answer_tip: newQuestion.aiHints,
          });
          setQualificationQuestions([
            ...qualificationQuestions,
            { ...newQuestion, id: res.data.id },
          ]);
          getQuestion();
          toast.success("Question added");
        } catch (e) {
          toast.error("Failed to add question");
        }
      }
      setNewQuestion({ prompt: "", aiHints: "" });
    }
  };

  const handleEditQuestion = (question) => {
    setNewQuestion({
      prompt: question.prompt,
      aiHints: question.aiHints || "",
    });
    setEditingQuestionId(question.id);
  };

  const handleCancelEditQuestion = () => {
    setNewQuestion({ prompt: "", aiHints: "" });
    setEditingQuestionId(null);
  };

  const handleDeleteQuestion = async (id) => {
    // Optionally implement delete API if available

    deleteQuestions(agentId || voiceAgentId, id)
      .then((res) => {
        toast.success("Question deleted");
        setQualificationQuestions(
          qualificationQuestions.filter((q) => q.id !== id)
        );
        if (editingQuestionId === id) {
          setNewQuestion({ prompt: "", aiHints: "" });
          setEditingQuestionId(null);
        }
      })
      .catch((e) => {
        toast.error(
          e.response?.data?.detail ||
          e.response?.data?.message ||
          "Failed to delete question"
        );
      });
  };

  const handleReorderQuestions = (startIndex, endIndex) => {
    const result = Array.from(qualificationQuestions);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    setQualificationQuestions(result);
  };

  const handleAddIntent = async () => {
    const id = agentId || voiceAgentId;

    if (!id) {
      toast.error("Please complete Step 1 first to create an agent.");
      return;
    }

    if (
      newIntent.name?.trim() &&
      (newIntent.label?.trim() || newIntent.criteria?.trim())
    ) {
      setIsSubmitting(true);
      if (editingIntentId) {
        // Update existing intent
        try {
          await updateIntent(id, editingIntentId, {
            name: newIntent.name,
            description: newIntent.label || newIntent.criteria,
            agent_call_transfer: newIntent.enableLiveTransfer,
          });
          setIntents(
            intents.map((intent) =>
              intent.id === editingIntentId
                ? { ...newIntent, id: editingIntentId }
                : intent
            )
          );
          getIntent(id);
          toast.success("Intent updated");
          setEditingIntentId(null);
        } catch (e) {
          console.error("Failed to update intent:", e);
          toast.error("Failed to update intent");
        } finally {
          setIsSubmitting(false);
        }
      } else {
        // Add new intent
        try {
          const res = await createIntent(id, {
            name: newIntent.name,
            description: newIntent.label || newIntent.criteria,
            agent_call_transfer: newIntent.enableLiveTransfer,
          });
          setIntents([...intents, { ...newIntent, id: res.data.id }]);
          getIntent(id);
          toast.success("Intent added");
        } catch (e) {
          console.error("Failed to add intent:", e);
          toast.error("Failed to add intent");
        } finally {
          setIsSubmitting(false);
        }
      }
      setNewIntent({
        name: "",
        label: "",
        criteria: "",
        enableLiveTransfer: false,
      });
    } else {
      toast.error("Please fill in the intent name and criteria");
    }
  };

  const handleEditIntent = (intent) => {
    setNewIntent({
      name: intent.name,
      label: intent.label || "",
      criteria: intent.criteria || "",
      enableLiveTransfer: intent.enableLiveTransfer || false,
    });
    setEditingIntentId(intent.id);
  };

  const handleCancelEditIntent = () => {
    setNewIntent({
      name: "",
      label: "",
      criteria: "",
      enableLiveTransfer: false,
    });
    setEditingIntentId(null);
  };

  const handleDeleteIntent = async (id) => {
    // Optionally implement delete API if available
    if (id === "default-hot") return;
    deleteIntent(agentId || voiceAgentId, id)
      .then((res) => {
        toast.success("Delete Successfully");
        setIntents(intents.filter((intent) => intent.id !== id));
        if (editingIntentId === id) {
          setNewIntent({
            name: "",
            label: "",
            criteria: "",
            enableLiveTransfer: false,
          });
          setEditingIntentId(null);
        }
      })
      .catch((err) => {
        toast.error(
          err.response?.data?.detail ||
          err.response?.data?.message ||
          "Failed to delete intent"
        );
      });
  };

  const handleIntentChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewIntent({
      ...newIntent,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleGlobalSettingsChange = (e) => {
    const { name, checked } = e.target;
    setGlobalSettings({
      ...globalSettings,
      [name]: checked,
    });
  };

  const nextStep = async () => {
    // if (voiceAgentId && !agentId) {
    //   setCurrentStep(currentStep + 1);
    //   return;
    // }
    if (currentStep === 1) {
      // Validate form
      if (!validateForm()) {
        toast.error("Please fix the errors before submitting");
        return;
      }
      setIsSubmitting(true);
      // Format days allowed for API
      const daysAllowedArray = Object.entries(campaignDetails.daysAllowed)
        .filter(([_, isSelected]) => isSelected)
        .map(([day]) => day.toLowerCase());
      // Prepare data for API - Voice Agent creation
      // VAPI credentials are handled by backend environment variables
      const campaignData = {
        name: campaignDetails.name,
        description: campaignDetails.description,
        agent_type: campaignDetails.agentType,
        phone_number: "", // Not needed - VAPI uses environment config
        start_time: normalizeTime(campaignDetails.startTime),
        end_time: normalizeTime(campaignDetails.endTime),
        days_allowed: daysAllowedArray.join(","),
        max_call_limit: campaignDetails.callThrottleLimit,
        voice_tone: campaignDetails.voiceTone,
        // twilio_integeration is optional - VAPI uses DEFAULT_VAPI_ASSISTANT_ID from env
      };
      try {
        let response = null;
        if (agentId || voiceAgentId) {
          response = await updateAgent(agentId || voiceAgentId, campaignData);
        } else {
          response = await createAgent(campaignData);
        }
        const newId = response.data?.voice_agent || response.data?.id || voiceAgentId;
        setVoiceAgentId(newId);
        localStorage.setItem("voice_agent", newId);
        setCurrentStep(currentStep + 1);
      } catch (error) {
        console.error("Error saving campaign:", error);
        toast.error(
          error.response?.data?.detail ||
          error.response?.data?.message ||
          "Failed to save campaign. Please try again."
        );
      } finally {
        setIsSubmitting(false);
      }
    } else if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const validateForm = () => {
    const errors = {};

    // Validate required fields
    if (!campaignDetails.name?.trim()) {
      errors.name = "Campaign name is required";
    }

    // callerId/Voice Assistant is NOT required - VAPI uses environment variables

    if (!campaignDetails.startTime) {
      errors.startTime = "Start time is required";
    }

    if (!campaignDetails.endTime) {
      errors.endTime = "End time is required";
    }
    // Start/End time validation
    if (campaignDetails.startTime && campaignDetails.endTime) {
      const [startHour, startMinute] = campaignDetails.startTime
        .split(":")
        .map(Number);
      const [endHour, endMinute] = campaignDetails.endTime
        .split(":")
        .map(Number);
      const start = startHour * 60 + startMinute;
      const end = endHour * 60 + endMinute;
      if (end <= start) {
        errors.endTime = "End time must be after start time";
      }
    }

    if (!campaignDetails.callThrottleLimit) {
      errors.callThrottleLimit = "Call throttle limit is required";
    } else if (Number(campaignDetails.callThrottleLimit) <= 0) {
      errors.callThrottleLimit =
        "Call throttle limit must be a positive number";
    }

    if (!campaignDetails.voiceTone) {
      errors.voiceTone = "Voice tone is required";
    }

    if (!campaignDetails.agentType) {
      errors.agentType = "Agent type is required";
    }

    // Check if at least one day is selected
    const anyDaySelected = Object.values(campaignDetails.daysAllowed).some(
      (value) => value
    );
    if (!anyDaySelected) {
      errors.daysAllowed = "At least one day must be selected";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveScript = async () => {
    if (!step4Data.category || !step4Data.description.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);
    const id = agentId || voiceAgentId;

    try {
      const scriptData = {
        category: step4Data.category,
        description: step4Data.description,
      };

      let response;
      if (editingScriptId) {
        // Update existing script
        response = await updateScript(id, editingScriptId, scriptData);
        // console.log({ response });
        setStep4Data({
          category: "",
          description: "",
        });
        setEditingScriptId(null);
        // setScripts(
        //   scripts.map((script) =>
        //     script.id === editingScriptId ? { ...scriptData } : script
        //   )
        // );
        getScript();
        toast.success("Script updated successfully");
      } else {
        // Create new script
        response = await createScript(id, scriptData);
        setScripts([...scripts, response.data]);
        setEditingScriptId(response.data.id);
        setStep4Data({
          category: "",
          description: "",
        });
        setEditingScriptId(null);
        getScript();
        toast.success("Script created successfully");
      }
    } catch (error) {
      console.error("Error saving script:", error);
      toast.error(
        error.response?.data?.detail ||
        error.response?.data?.message ||
        "Failed to save script"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteScript = async () => {
    if (!editingScriptId) return;

    const id = agentId || voiceAgentId;
    try {
      await deleteScript(id, editingScriptId);
      setScripts(scripts.filter((script) => script.id !== editingScriptId));
      setEditingScriptId(null);
      setStep4Data({
        category: "greeting",
        description: "",
      });
      toast.success("Script deleted successfully");
    } catch (error) {
      console.error("Error deleting script:", error);
      toast.error(
        error.response?.data?.detail ||
        error.response?.data?.message ||
        "Failed to delete script"
      );
    }
  };

  const handleCreateCampaign = async () => {
    // setIsSubmitting(true);
    // try {
    //   // Save script first if we're on step 4
    //   if (currentStep === 4) {
    //     // Validate script data
    //     // if (!step4Data.category) {
    //     //   toast.error("Please select a category");
    //     //   setIsSubmitting(false);
    //     //   return;
    //     // }

    //     // Save the script before navigating
    //     const id = agentId || voiceAgentId;
    //     const scriptData = {
    //       category: step4Data.category,
    //       description: step4Data.description || "",
    //     };

    //     if (editingScriptId) {
    //       await updateScript(id, editingScriptId, scriptData);
    //       toast.success("Script updated successfully");
    //     } else {
    //       await createScript(id, scriptData);
    //       toast.success("Script created successfully");
    //     }
    //   }
    localStorage.setItem("voice_agent", null);
    // Navigate to voice agents page
    navigate("/voice-agents");
    // } catch (error) {
    //   console.error("Error saving script:", error);
    //   toast.error(error.response?.data?.detail || "Failed to save script");
    // } finally {
    //   setIsSubmitting(false);
    // }
  };

  const resetForm = () => {
    setCurrentStep(1);
    setCampaignDetails({
      name: "",
      description: "",
      agentType: "buyer",
      callerId: "",
      startTime: "09:00",
      endTime: "17:00",
      daysAllowed: {
        Monday: true,
        Tuesday: true,
        Wednesday: true,
        Thursday: true,
        Friday: true,
        Saturday: false,
        Sunday: false,
      },
      callThrottleLimit: 500,
      voiceTone: "Friendly",
    });
    setFormErrors({});
    setQualificationQuestions([]);
    setNewQuestion({ prompt: "", aiHints: "" });
    setEditingQuestionId(null);
    setIntents([
      {
        id: "default-hot",
        name: "hot",
        label: "Owner says he is willing to sell",
        criteria: "",
        enableLiveTransfer: false,
      },
    ]);
    setNewIntent({
      name: "",
      label: "",
      criteria: "",
      enableLiveTransfer: false,
    });
    setEditingIntentId(null);
    setGlobalSettings({
      enableLiveTransfer: false,
      callOutcomeLogging: true,
    });
  };

  return (
    <div className="max-w-5xl mx-auto bg-white rounded-lg shadow-sm p-3 sm:p-6">
      {/* Header */}
      <div className="text-center mb-6">
        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-collings-aquamarine rounded-full flex items-center justify-center mx-auto mb-3">
          <Phone className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
        </div>
        <h1 className="text-xl sm:text-3xl font-semibold text-gray-800">
          Campaign Setup
        </h1>
        <p className="text-xs sm:text-base text-gray-500 mt-1">
          Configure your AI campaign in easy steps
        </p>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center mb-6 overflow-x-auto pb-4 no-scrollbar -mx-3 px-3 sm:px-0 sm:mx-0 sm:justify-center">
        {/* Step 1 */}
        <div className="flex items-center flex-shrink-0">
          <div
            className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center transition-colors ${currentStep >= 1
              ? "bg-collings-aquamarine text-white"
              : "bg-gray-200 text-gray-600"
              }`}
          >
            {currentStep > 1 ? <Check className="w-5 h-5" /> : "1"}
          </div>
          <div className={`ml-2 mr-2 sm:mr-4 transition-all ${currentStep === 1 ? 'block' : 'hidden md:block'}`}>
            <div className="font-medium whitespace-nowrap text-xs sm:text-sm">Campaign</div>
            <div className="text-[10px] text-gray-500 whitespace-nowrap hidden lg:block">
              Details
            </div>
          </div>
        </div>

        <div className="w-4 sm:w-8 h-px bg-gray-200 mx-2 flex-shrink-0"></div>

        {/* Step 2 */}
        <div className="flex items-center flex-shrink-0">
          <div
            className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center transition-colors ${currentStep >= 2
              ? "bg-collings-aquamarine text-white"
              : "bg-gray-200 text-gray-600"
              }`}
          >
            {currentStep > 2 ? <Check className="w-5 h-5" /> : "2"}
          </div>
          <div className={`ml-2 mr-2 sm:mr-4 transition-all ${currentStep === 2 ? 'block' : 'hidden md:block'}`}>
            <div className="font-medium whitespace-nowrap text-xs sm:text-sm">Qualifications</div>
            <div className="text-[10px] text-gray-500 whitespace-nowrap hidden lg:block">
              AI Questions
            </div>
          </div>
        </div>

        <div className="w-4 sm:w-8 h-px bg-gray-200 mx-2 flex-shrink-0"></div>

        {/* Step 3 */}
        <div className="flex items-center flex-shrink-0">
          <div
            className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center transition-colors ${currentStep >= 3
              ? "bg-collings-aquamarine text-white"
              : "bg-gray-200 text-gray-600"
              }`}
          >
            {currentStep > 3 ? <Check className="w-5 h-5" /> : "3"}
          </div>
          <div className={`ml-2 mr-2 sm:mr-4 transition-all ${currentStep === 3 ? 'block' : 'hidden md:block'}`}>
            <div className="font-medium whitespace-nowrap text-xs sm:text-sm">Intent</div>
            <div className="text-[10px] text-gray-500 whitespace-nowrap hidden lg:block">
              Handoffs
            </div>
          </div>
        </div>

        <div className="w-4 sm:w-8 h-px bg-gray-200 mx-2 flex-shrink-0"></div>

        {/* Step 4 */}
        <div className="flex items-center flex-shrink-0 pr-4">
          <div
            className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center transition-colors ${currentStep === 4
              ? "bg-collings-aquamarine text-white"
              : "bg-gray-200 text-gray-600"
              }`}
          >
            4
          </div>
          <div className={`ml-2 transition-all ${currentStep === 4 ? 'block' : 'hidden md:block'}`}>
            <div className="font-medium whitespace-nowrap text-xs sm:text-sm">Script</div>
            <div className="text-[10px] text-gray-500 whitespace-nowrap hidden lg:block">
              Call scripts
            </div>
          </div>
        </div>
      </div>


      {/* Step Content */}
      <div className="bg-white rounded-lg p-2 sm:p-6">
        {currentStep === 1 && (
          <div>
            <div className="flex flex-col items-center mb-6 justify-center">
              <div className="w-10 h-10 bg-collings-aquamarine bg-opacity-10 rounded-md flex items-center justify-center">
                <Phone className="w-5 h-5 text-collings-aquamarine" />
              </div>
              <div className="ml-4 ">
                <h2 className="text-lg sm:text-xl text-center my-4 font-medium text-gray-800">
                  Campaign Details
                </h2>
                <p className="text-sm text-gray-600">
                  Define your campaign's basic information and calling
                  parameters
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="border border-collings-aquamarine  p-3 rounded-lg">
                <h3 className="flex items-center text-sm font-medium text-gray-700 mb-4">
                  <span className="w-5 h-5 inline-flex items-center justify-center rounded-full border border-gray-300 mr-2 text-xs">
                    1
                  </span>
                  Basic Information
                </h3>
                <div className="mb-4">
                  <label
                    htmlFor="campaignName"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Agent Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="campaignName"
                    name="name"
                    value={campaignDetails.name}
                    onChange={handleCampaignDetailsChange}
                    placeholder="e.g. July Owners Outreach"
                    className={`w-full px-3 py-2 border ${formErrors.name
                      ? "border-red-500"
                      : "border-collings-aquamarine"
                      } rounded-md focus:outline-none focus:ring-2 focus:ring-collings-aquamarine focus:border-transparent`}
                  />
                  {formErrors.name && (
                    <p className="text-red-500 text-xs mt-1">
                      {formErrors.name}
                    </p>
                  )}
                </div>
                <div className="mb-4">
                  <label
                    htmlFor="description"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Description
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={campaignDetails.description}
                    onChange={handleCampaignDetailsChange}
                    placeholder="Brief description of campaign goals and audience..."
                    className="w-full px-3 py-2 border border-collings-aquamarine rounded-md focus:outline-none focus:ring-2 focus:ring-collings-aquamarine focus:border-transparent h-24"
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">
                    Optional: internal note about this campaign
                  </p>
                </div>
                <div className="mb-4">
                  <label
                    htmlFor="agentType"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Agent Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="agentType"
                    name="agentType"
                    value={campaignDetails.agentType}
                    onChange={handleCampaignDetailsChange}
                    className={`w-full px-3 py-2 border ${formErrors.agentType
                      ? "border-red-500"
                      : "border-collings-aquamarine"
                      } rounded-md focus:outline-none focus:ring-2 focus:ring-collings-aquamarine focus:border-transparent`}
                  >
                    <option value="buyer">Buyer</option>
                    <option value="seller">Seller</option>
                    <option value="general">General</option>
                  </select>
                  {formErrors.agentType && (
                    <p className="text-red-500 text-xs mt-1">{formErrors.agentType}</p>
                  )}
                </div>

                <div className="mb-4">
                  <label
                    htmlFor="voiceTone"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Voice Tone <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="voiceTone"
                    name="voiceTone"
                    value={campaignDetails.voiceTone}
                    onChange={handleCampaignDetailsChange}
                    className={`w-full px-3 py-2 border ${formErrors.voiceTone
                      ? "border-red-500"
                      : "border-collings-aquamarine"
                      } rounded-md focus:outline-none focus:ring-2 focus:ring-collings-aquamarine focus:border-transparent`}
                  >
                    <option value="">Select a voice tone</option>
                    <option value="Friendly">Friendly</option>
                    <option value="Professional">Professional</option>
                    <option value="Casual">Casual</option>
                    <option value="Formal">Formal</option>
                    <option value="Enthusiastic">Enthusiastic</option>
                  </select>
                  {formErrors.voiceTone && (
                    <p className="text-red-500 text-xs mt-1">
                      {formErrors.voiceTone}
                    </p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    Select the tone of voice for the AI agent
                  </p>
                </div>
                {/* Voice Agent will be created with this campaign - no pre-configuration needed */}
                {/* VAPI credentials are configured via environment variables on the backend */}
              </div>

              <div>
                <div className="border border-collings-aquamarine  p-3 rounded-lg">
                  <h3 className="flex items-center text-sm font-medium text-gray-700 mb-4">
                    <span className="w-5 h-5 inline-flex items-center justify-center rounded-full border border-gray-300 mr-2 text-xs">
                      2
                    </span>
                    Calling Schedule
                  </h3>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label
                        htmlFor="startTime"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Start Time <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type="time"
                          id="startTime"
                          name="startTime"
                          value={campaignDetails.startTime}
                          onChange={handleCampaignDetailsChange}
                          className={`w-full px-3 py-2 border ${formErrors.startTime
                            ? "border-red-500"
                            : "border-collings-aquamarine"
                            } rounded-md focus:outline-none focus:ring-2 focus:ring-collings-aquamarine focus:border-transparent`}
                        />
                      </div>
                      {formErrors.startTime && (
                        <p className="text-red-500 text-xs mt-1">
                          {formErrors.startTime}
                        </p>
                      )}
                    </div>
                    <div>
                      <label
                        htmlFor="endTime"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        End Time <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type="time"
                          id="endTime"
                          name="endTime"
                          value={campaignDetails.endTime}
                          onChange={handleCampaignDetailsChange}
                          className={`w-full px-3 py-2 border ${formErrors.endTime
                            ? "border-red-500"
                            : "border-collings-aquamarine"
                            } rounded-md focus:outline-none focus:ring-2 focus:ring-collings-aquamarine focus:border-transparent`}
                        />
                      </div>
                      {formErrors.endTime && (
                        <p className="text-red-500 text-xs mt-1">
                          {formErrors.endTime}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Days Allowed <span className="text-red-500">*</span>
                    </label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-2 lg:grid-cols-4 gap-2">
                      {Object.keys(campaignDetails.daysAllowed).map((day) => (
                        <div key={day} className="flex items-center">
                          <input
                            type="checkbox"
                            id={day}
                            checked={campaignDetails.daysAllowed[day]}
                            onChange={() => handleDayToggle(day)}
                            className="h-4 w-4 text-collings-aquamarine focus:ring-collings-aquamarine border-collings-aquamarine rounded cursor-pointer"
                          />
                          <label
                            htmlFor={day}
                            className="ml-2 text-xs text-gray-700 cursor-pointer"
                          >
                            {day}
                          </label>
                        </div>
                      ))}
                    </div>
                    {formErrors.daysAllowed && (
                      <p className="text-red-500 text-xs mt-1">
                        {formErrors.daysAllowed}
                      </p>
                    )}
                  </div>
                </div>
                <div className="border border-collings-aquamarine  p-3 rounded-lg mt-10">
                  <h3 className="flex items-center text-sm font-medium text-gray-700 mb-4">
                    <span className="w-5 h-5 inline-flex items-center justify-center rounded-full border border-gray-300 mr-2 text-xs">
                      3
                    </span>
                    Performance Settings
                  </h3>
                  <div className="mb-4">
                    <label
                      htmlFor="callThrottleLimit"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Call Throttle Limit{" "}
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      id="callThrottleLimit"
                      name="callThrottleLimit"
                      value={campaignDetails.callThrottleLimit}
                      onChange={handleCampaignDetailsChange}
                      className={`w-full px-3 py-2 border ${formErrors.callThrottleLimit
                        ? "border-red-500"
                        : "border-collings-aquamarine"
                        } rounded-md focus:outline-none focus:ring-2 focus:ring-collings-aquamarine focus:border-transparent`}
                    />
                    {formErrors.callThrottleLimit && (
                      <p className="text-red-500 text-xs mt-1">
                        {formErrors.callThrottleLimit}
                      </p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      Maximum calls per hour to prevent system overload
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {currentStep === 2 && (
          <div>
            <div className="flex items-center mb-6 justify-center flex-col">
              <div className="w-10 h-10 bg-collings-aquamarine bg-opacity-10 rounded-md flex items-center justify-center">
                <div className="w-5 h-5 text-collings-aquamarine flex items-center justify-center">
                  <MessageSquare />
                </div>
              </div>
              <div className="ml-4">
                <h2 className="text-lg sm:text-xl font-medium text-center my-3 text-gray-800">
                  Qualification Questions
                </h2>
                <p className="text-sm text-gray-600 text-center">
                  Define the questions your AI will ask to qualify leads
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="border border-collings-aquamarine rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-700 mb-4">
                  Question List
                </h3>
                <p className="text-xs text-gray-500 mb-4">
                  Questions will be asked in order. Drag to reorder.
                </p>

                {qualificationQuestions.length === 0 ? (
                  <div className=" rounded-lg p-8 text-center">
                    <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                      <HelpCircle className="w-8 h-8 text-gray-400" />
                    </div>
                    <p className="text-gray-500 mb-2">No questions added yet</p>
                    <p className="text-xs text-gray-400">
                      Add your first qualification question to get started
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {qualificationQuestions.map((question, index) => (
                      <div
                        key={question.id}
                        className={`border ${editingQuestionId === question.id
                          ? "border-collings-aquamarine"
                          : "border-blue-200"
                          } rounded-lg p-4 flex items-start`}
                      >
                        <div className="mr-3 mt-1 text-gray-400">
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-gray-800">
                            {question.prompt}
                          </p>
                          {question.aiHints && (
                            <p className="text-xs text-gray-500 mt-1">
                              Hint: {question.aiHints}
                            </p>
                          )}
                        </div>
                        <div className="flex space-x-2">
                          <button
                            className="text-gray-400 hover:text-collings-aquamarine"
                            onClick={() => handleEditQuestion(question)}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-4 w-4"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                              />
                            </svg>
                          </button>
                          <button
                            className="text-gray-400 hover:text-red-500"
                            onClick={() => handleDeleteQuestion(question.id)}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-4 w-4 "
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <div className="border border-collings-aquamarine rounded-lg p-4">
                  <h3 className="text-sm font-medium text-gray-700 mb-4">
                    Add New Question
                  </h3>
                  <div className="mb-4">
                    <label
                      htmlFor="questionPrompt"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Question Prompt
                    </label>
                    <textarea
                      id="questionPrompt"
                      name="prompt"
                      value={newQuestion.prompt}
                      onChange={handleQuestionInputChange}
                      placeholder="What should the AI ask the prospect?"
                      className="w-full px-3 py-2 border border-collings-aquamarine rounded-md focus:outline-none focus:ring-2 focus:ring-collings-aquamarine focus:border-transparent h-24"
                    ></textarea>
                  </div>
                  <div className="mb-6">
                    <label
                      htmlFor="aiHints"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      AI Hints (Optional)
                    </label>
                    <textarea
                      id="aiHints"
                      name="aiHints"
                      value={newQuestion.aiHints}
                      onChange={handleQuestionInputChange}
                      placeholder="e.g. Listen for yes/no/maybe responses"
                      className="w-full px-3 py-2 border border-collings-aquamarine rounded-md focus:outline-none focus:ring-2 focus:ring-collings-aquamarine focus:border-transparent h-24"
                    ></textarea>
                    <p className="text-xs text-gray-500 mt-1">
                      Guide the AI on what to listen for
                    </p>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={handleAddQuestion}
                      className="flex-1 py-2 px-4 bg-collings-aquamarine text-white rounded-md hover:bg-opacity-90 transition-colors flex items-center justify-center"
                    >
                      {editingQuestionId ? (
                        <>
                          <Check className="w-4 h-4 mr-2" />
                          Update Question
                        </>
                      ) : (
                        <>
                          <Plus className="w-4 h-4 mr-2" />
                          Add Question
                        </>
                      )}
                    </button>
                    {editingQuestionId && (
                      <button
                        onClick={handleCancelEditQuestion}
                        className="py-2 px-4 border border-collings-aquamarine text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
                <div className="mt-8  bg-blue-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-blue-800 mb-2">
                    Best Practices
                  </h4>
                  <ul className="text-xs text-blue-700 space-y-2">
                    <li className="flex items-start">
                      <span className="text-blue-500 mr-2">•</span> Keep
                      questions conversational and natural
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-500 mr-2">•</span> Ask one
                      thing at a time
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-500 mr-2">•</span> Use
                      open-ended questions when possible
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-500 mr-2">•</span> Provide
                      clear hints for AI interpretation
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {currentStep === 3 && (
          <div>
            <div className="flex items-center mb-6 flex-col justify-center">
              <div className="w-10 h-10 bg-collings-aquamarine bg-opacity-10 rounded-md flex items-center justify-center">
                <div className="w-5 h-5 text-collings-aquamarine flex items-center justify-center">
                  <Disc2 />
                </div>
              </div>
              <div className="ml-4">
                <h2 className="text-lg sm:text-xl font-medium text-center my-3 text-gray-800">
                  Intent Classification & Handoff
                </h2>
                <p className="text-sm text-gray-600">
                  Define how to interpret prospect responses and handle next
                  steps
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="border border-collings-aquamarine rounded-lg p-4">
                  <h3 className="text-sm font-medium text-gray-700 mb-4">
                    Intent Labels & Rules
                  </h3>
                  <p className="text-xs text-gray-500 mb-4">
                    Define how the AI should classify prospect responses
                  </p>

                  <div className="space-y-3">
                    {intents.map((intent) => (
                      <div
                        key={intent.id || intent.name}
                        className={`border ${editingIntentId === intent.id
                          ? "border-collings-aquamarine"
                          : "border-blue-200"
                          } rounded-lg p-3 sm:p-4`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center">
                            <span
                              className={`inline-block w-2 h-2 rounded-full mr-2
                                ${intent.name?.toLowerCase() === "hot"
                                  ? "bg-red-500"
                                  : ""
                                }
                                ${intent.name?.toLowerCase() === "warm"
                                  ? "bg-orange-500"
                                  : ""
                                }
                                ${intent.name?.toLowerCase() === "cold"
                                  ? "bg-blue-500"
                                  : ""
                                }
                              `}
                            ></span>
                            <span className="text-sm font-medium">
                              {intent.name}
                            </span>
                          </div>
                          <div className="flex space-x-2">
                            <button
                              className="text-gray-400 hover:text-collings-aquamarine"
                              onClick={() => handleEditIntent(intent)}
                              disabled={intent.id === "default-hot"}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-4 w-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                                />
                              </svg>
                            </button>
                            <button
                              className={`text-gray-400 ${intent.id === "default-hot"
                                ? "opacity-50 cursor-not-allowed"
                                : "hover:text-red-500"
                                }`}
                              onClick={() => handleDeleteIntent(intent.id)}
                              disabled={intent.id === "default-hot"}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-4 w-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                />
                              </svg>
                            </button>
                          </div>
                        </div>
                        <p className="text-sm text-gray-700">{intent.label}</p>
                        {intent.criteria && (
                          <p className="text-xs text-gray-500 mt-1">
                            Criteria: {intent.criteria}
                          </p>
                        )}
                        {intent.enableLiveTransfer && (
                          <div className="mt-2 text-xs inline-flex items-center px-2 py-1 bg-green-100 text-green-800 rounded-full">
                            <span className="w-1.5 h-1.5 bg-green-500 rounded-full mr-1"></span>
                            Live Transfer Enabled
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <div className="border border-collings-aquamarine rounded-lg p-4">
                  <h3 className="text-sm font-medium text-gray-700 mb-4">
                    Add New Intent
                  </h3>
                  <div className="mb-4">
                    <label
                      htmlFor="intentName"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Intent Name
                    </label>
                    <input
                      type="text"
                      id="intentName"
                      name="name"
                      value={newIntent.name}
                      onChange={handleIntentChange}
                      placeholder="e.g. Hot, Warm, Cold"
                      className="w-full px-3 py-2 border border-collings-aquamarine rounded-md focus:outline-none focus:ring-2 focus:ring-collings-aquamarine focus:border-transparent"
                      disabled={editingIntentId === "default-hot"}
                    />
                  </div>
                  <div className="mb-4">
                    <label
                      htmlFor="intentCriteria"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Classification Criteria
                    </label>
                    <textarea
                      id="intentCriteria"
                      name="criteria"
                      value={newIntent.criteria}
                      onChange={handleIntentChange}
                      placeholder="e.g. Prospect mentions they want to sell in the next 3 months"
                      className="w-full px-3 py-2 border border-collings-aquamarine rounded-md focus:outline-none focus:ring-2 focus:ring-collings-aquamarine focus:border-transparent h-24"
                    ></textarea>
                    <p className="text-xs text-gray-500 mt-1">
                      Describe when this intent should be applied
                    </p>
                  </div>
                  <div className="mb-4">
                    <div className="flex items-center mb-2">
                      <input
                        type="checkbox"
                        id="enableLiveTransfer"
                        name="enableLiveTransfer"
                        checked={newIntent.enableLiveTransfer}
                        onChange={handleIntentChange}
                        className="h-4 w-4 text-collings-aquamarine focus:ring-collings-aquamarine border-gray-300 rounded"
                      />
                      <label
                        htmlFor="enableLiveTransfer"
                        className="ml-2 text-sm text-gray-700"
                      >
                        Enable Live Transfer
                      </label>
                    </div>
                    <p className="text-xs text-gray-500 ml-6">
                      Transfer calls with this intent to agents
                    </p>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={handleAddIntent}
                      className="flex-1 py-2 px-4 bg-collings-aquamarine text-white rounded-md hover:bg-opacity-90 transition-colors flex items-center justify-center"
                      disabled={
                        !newIntent.name?.trim() ||
                        (!newIntent.label?.trim() &&
                          !newIntent.criteria?.trim())
                      }
                    >
                      {editingIntentId ? (
                        <>
                          <Check className="w-4 h-4 mr-2" />
                          Update Intent
                        </>
                      ) : (
                        <>
                          <Plus className="w-4 h-4 mr-2" />
                          Add Intent
                        </>
                      )}
                    </button>
                    {editingIntentId && (
                      <button
                        onClick={handleCancelEditIntent}
                        className="py-2 px-4 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
                <div className="mt-8 bg-blue-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-blue-800 mb-2">
                    Common Intent Labels
                  </h4>
                  <ul className="text-xs text-blue-700 space-y-2">
                    <li className="flex items-start">
                      <span className="text-red-500 mr-2">•</span>{" "}
                      <strong>Hot:</strong> Ready to sell right now
                    </li>
                    <li className="flex items-start">
                      <span className="text-orange-500 mr-2">•</span>{" "}
                      <strong>Warm:</strong> Interested but not urgent
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-500 mr-2">•</span>{" "}
                      <strong>Cold:</strong> No immediate interest
                    </li>
                    <li className="flex items-start">
                      <span className="text-purple-500 mr-2">•</span>{" "}
                      <strong>Speak to Agent:</strong> Requests human contact
                    </li>
                    <li className="flex items-start">
                      <span className="text-gray-500 mr-2">•</span>{" "}
                      <strong>Not Interested:</strong> Decline offer
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {currentStep === 4 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="border border-collings-aquamarine p-3 rounded-lg">
              <h3 className="flex items-center text-sm font-medium text-gray-700 mb-4">
                <span className="w-5 h-5 inline-flex items-center justify-center rounded-full border border-gray-300 mr-2 text-xs">
                  4
                </span>
                Script
              </h3>
              {isLoadingScripts ? (
                <div className="flex justify-center items-center h-40">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-collings-aquamarine"></div>
                </div>
              ) : (
                <>
                  <div className="mb-4">
                    <label
                      htmlFor="category"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Category <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="category"
                      name="category"
                      value={step4Data.category}
                      onChange={handleStep4Change}
                      className="w-full px-3 py-2 border border-collings-aquamarine rounded-md focus:outline-none focus:ring-2 focus:ring-collings-aquamarine focus:border-transparent"
                    >
                      <option value="greeting">Greeting</option>
                      <option value="follow up">Follow Up</option>
                      <option value="market update">Market Update</option>
                    </select>
                  </div>
                  <div className="mb-4">
                    <label
                      htmlFor="step4Description"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Description
                    </label>
                    <textarea
                      id="step4Description"
                      name="description"
                      value={step4Data.description}
                      onChange={handleStep4Change}
                      placeholder="Enter description for this category"
                      className="w-full px-3 py-2 border border-collings-aquamarine rounded-md focus:outline-none focus:ring-2 focus:ring-collings-aquamarine focus:border-transparent h-24"
                    ></textarea>
                  </div>
                  <div className="flex space-x-2 mt-4">
                    <button
                      onClick={handleSaveScript}
                      disabled={
                        isSubmitting ||
                        !step4Data.category ||
                        !step4Data.description.trim()
                      }
                      className="flex-1 py-2 px-4 bg-collings-aquamarine text-white rounded-md hover:bg-opacity-90 transition-colors flex items-center justify-center"
                    >
                      {isSubmitting ? (
                        "Saving..."
                      ) : editingScriptId ? (
                        <>
                          <Check className="w-4 h-4 mr-2" />
                          Update Script
                        </>
                      ) : (
                        <>
                          <Plus className="w-4 h-4 mr-2" />
                          Save Script
                        </>
                      )}
                    </button>
                    {editingScriptId && (
                      <button
                        onClick={() => {
                          setStep4Data({
                            category: "greeting",
                            description: "",
                          });
                          setEditingScriptId(null);
                        }}
                        disabled={isSubmitting}
                        className="py-2 px-4 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors flex items-center justify-center"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="border border-collings-aquamarine p-3 rounded-lg">
              <h3 className="text-sm font-medium text-gray-700 mb-4">
                Existing Scripts
              </h3>
              {scripts.length === 0 ? (
                <div className="rounded-lg p-8 text-center border border-dashed border-collings-aquamarine">
                  <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                    <MessageSquare className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-gray-500 mb-2">No scripts entered yet</p>
                  <p className="text-xs text-gray-400">
                    Create your first script using the form on the left
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {scripts.map((script) => (
                    <div
                      key={script.id}
                      className={`p-3 rounded-lg border ${editingScriptId === script.id
                        ? "border-collings-aquamarine bg-blue-50"
                        : "border-blue-200"
                        } flex flex-col xs:flex-row justify-between items-start xs:items-center hover:border-gray-300 gap-3`}
                    >
                      <div className="flex-1 ">
                        <div className="font-medium text-gray-800 capitalize">
                          {script.category}
                        </div>
                        <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                          {script.description}
                        </p>
                      </div>
                      <div className="flex items-end space-y-2 ml-2">
                        <button
                          className="text-gray-400 hover:text-collings-aquamarine"
                          title="Edit"
                          onClick={(e) => {
                            e.stopPropagation();
                            setStep4Data({
                              category: script.category.toLowerCase(),
                              description: script.description,
                            });
                            setEditingScriptId(script.id);
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                            />
                          </svg>
                        </button>
                        <button
                          className="text-gray-400 hover:text-red-500"
                          title="Delete"
                          onClick={async (e) => {
                            e.stopPropagation();
                            setIsSubmitting(true);
                            try {
                              await deleteScript(
                                agentId || voiceAgentId,
                                script.id
                              );
                              setScripts(
                                scripts.filter((s) => s.id !== script.id)
                              );
                              if (editingScriptId === script.id) {
                                setStep4Data({
                                  category: "greeting",
                                  description: "",
                                });
                                setEditingScriptId(null);
                              }
                              toast.success("Script deleted successfully");
                            } catch (error) {
                              toast.error(
                                error.response?.data?.detail ||
                                error.response?.data?.message ||
                                "Failed to delete script"
                              );
                            } finally {
                              setIsSubmitting(false);
                            }
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {/* Navigation Buttons */}
      <div className="flex flex-col sm:flex-row justify-between gap-4 mt-8">
        <button
          onClick={prevStep}
          className={`w-full sm:w-auto px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors ${currentStep === 1 ? "opacity-50 cursor-not-allowed" : ""
            }`}
          disabled={currentStep === 1}
        >
          Previous
        </button>
        {currentStep < 4 ? (
          <button
            onClick={nextStep}
            className={`w-full sm:w-auto px-6 py-2 bg-collings-aquamarine text-white rounded-md hover:bg-opacity-90 transition-colors ${isSubmitting ? "opacity-70 cursor-not-allowed" : ""
              }`}
          >
            {agentId
              ? "Update & Next"
              : isSubmitting
                ? "Saving..."
                : "Save & Next"}
          </button>
        ) : (
          <button
            onClick={handleCreateCampaign}
            disabled={isSubmitting}
            className={`w-full sm:w-auto px-6 py-2 bg-collings-aquamarine text-white rounded-md hover:bg-opacity-90 transition-colors ${isSubmitting ? "opacity-70 cursor-not-allowed" : ""
              }`}
          >
            {isSubmitting && agentId
              ? "Updating...."
              : agentId
                ? "Updating Campaign"
                : isSubmitting
                  ? "Creating..."
                  : "Create Campaign"}
          </button>
        )}
      </div>
    </div>
  );
};

export default CampaignSetupWizard;
